/**
 * Event Data Transformers
 * 
 * This module provides a unified transformation layer for converting between
 * different data formats. The BalloonEventModel serves as the canonical
 * intermediate representation.
 * 
 * Data flow:
 * - Draft API Response → BalloonEventModel → EventFormData
 * - Existing Balloon (BalloonEventModel) → EventFormData
 * - EventFormData → Draft API Payload (for saving)
 */

import { 
  BalloonEventModel, 
  BalloonEventModelDraftBuilder,
  EventDateModel,
  CategoryModel,
  Language,
  DescriptionType,
  ContentType,
  ContactType
} from 'ts-core';
import type { Description, Contact } from 'ts-core';
import { EventFormData } from '../types';
import { isVideoLinkUrl } from '../utils/imageUtils';

/**
 * Raw draft data structure from the API
 */
export interface DraftApiData {
  id: string;
  name?: string;
  categories?: Array<{ slug: string }>;
  descriptions?: Array<{ language: string; text: string; type: string }>;
  dates?: Array<{ startDate: string; endDate: string; notes?: string }>;
  location?: Array<{
    name?: string;
    address?: {
      street?: string;
      zip?: string;
      locality?: string;
      country?: string;
      region?: string;
    };
    coordinates?: { lat: number; lng: number };
  }>;
  images?: Array<{ url: string; caption?: string; copyright?: string }>;
  tags?: string[];
  contactInfos?: Array<{ type: string; content: string }>;
  organisers?: Array<{
    name: string;
    salutation?: string;
    location?: Array<{
      name: string;
      address: {
        street: string;
        zip: string;
        locality: string;
        country: string;
        region: string;
      };
      coordinates?: { lat: number; lng: number };
    }>;
    contactInfos?: Array<{ type: string; content: string }>;
  }>;
}

/**
 * Transform draft API response to BalloonEventModel
 * This normalizes draft data to the canonical event model
 */
export function transformDraftToBalloonEvent(
  _draftId: string,
  draft: DraftApiData,
  categoryResolver?: (slug: string) => CategoryModel | null
): BalloonEventModel {
  const builder = new BalloonEventModelDraftBuilder();

  // Basic info
  builder.title(draft.name || '');

  // Descriptions - must match ts-core Description interface
  if (draft.descriptions && draft.descriptions.length > 0) {
    const descriptions: Description[] = draft.descriptions.map(d => ({
      type: (d.type || DescriptionType.CollectiveDescriptionBody) as DescriptionType,
      contentType: ContentType.TEXT,
      content: d.text || '',
      languageIso: d.language || 'en'
    }));
    builder.description(descriptions);
  }

  // Categories - resolve slugs to full CategoryModel if resolver provided
  if (draft.categories && draft.categories.length > 0) {
    const resolvedCategories: CategoryModel[] = [];
    for (const cat of draft.categories) {
      if (cat.slug && categoryResolver) {
        const resolved = categoryResolver(cat.slug);
        if (resolved) {
          resolvedCategories.push(resolved);
        }
      }
    }
    builder.categories(resolvedCategories);
  }

  // Dates
  if (draft.dates && draft.dates.length > 0) {
    builder.dates(
      draft.dates.map(d => new EventDateModel(
        new Date(d.startDate),
        new Date(d.endDate)
      ))
    );
  }

  // Location - use separate builder methods
  // Always set default coordinates so BalloonEventModel constructor doesn't crash
  const location = draft.location?.[0];
  if (location) {
    if (location.name) {
      builder.locationName(location.name);
    }
    builder.location({
      lat: location.coordinates?.lat || 0,
      lng: location.coordinates?.lng || 0
    });
    if (location.address) {
      builder.address({
        street: location.address.street || '',
        zip: location.address.zip || '',
        locality: location.address.locality || '',
        country: location.address.country || '',
        region: location.address.region || ''
      });
    }
  } else {
    // No location at all — provide zeroed coordinates to prevent constructor crash
    builder.location({ lat: 0, lng: 0 });
  }

  // Images - ts-core ImageSource has url, text, copyright
  if (draft.images && draft.images.length > 0) {
    builder.images(
      draft.images.map(img => ({
        url: img.url,
        text: img.caption, // API uses 'caption', ts-core uses 'text'
        copyright: img.copyright
      }))
    );
  }

  // Tags
  if (draft.tags && draft.tags.length > 0) {
    builder.tags(draft.tags);
  }

  // Contacts - ts-core Contact uses 'content' and ContactType enum
  if (draft.contactInfos && draft.contactInfos.length > 0) {
    const contacts: Contact[] = draft.contactInfos.map(c => ({
      type: c.type as ContactType, // Cast string to ContactType enum
      content: c.content
    }));
    builder.contacts(contacts);
  }

  // Organizer
  if (draft.organisers && draft.organisers.length > 0) {
    const org = draft.organisers[0];
    builder.organizer({
      name: org.name || '',
      designation: '',
      salutation: org.salutation || '',
      contacts: org.contactInfos?.map(c => ({
        type: c.type as ContactType,
        content: c.content
      })) || [],
      location: org.location?.map(loc => ({
        name: loc.name || '',
        coordinates: loc.coordinates || { lat: 0, lng: 0 },
        address: {
          street: loc.address?.street || '',
          zip: loc.address?.zip || '',
          locality: loc.address?.locality || '',
          country: loc.address?.country || '',
          region: loc.address?.region || ''
        }
      })) || []
    });
  }

  return builder.build();
}

/**
 * Transform BalloonEventModel to EventFormData for the form
 * This is the single point of conversion from the canonical model to form state
 */
export function transformBalloonEventToFormData(
  event: BalloonEventModel,
  options: { isDraft?: boolean; draftId?: string } = {}
): EventFormData {
  const { isDraft = false, draftId } = options;

  // Get description text and structured descriptions
  let descriptionText = '';
  let subtitleText = '';
  const structuredDescriptions: Array<{ languageIso: string; type: string; contentType: string; content: string }> = [];
  
  try {
    // Get main description
    const mainDesc = event._complexDescription?.getDescription?.(DescriptionType.CollectiveDescriptionBody, Language.en);
    descriptionText = mainDesc || event._complexDescription?.getDescription?.(DescriptionType.CollectiveDescriptionBody, Language.de) || '';
    
    // Get subtitle
    const subtitle = event._complexDescription?.getDescription?.(DescriptionType.Subtitle, Language.en);
    subtitleText = subtitle || event._complexDescription?.getDescription?.(DescriptionType.Subtitle, Language.de) || '';
    
    // Get all descriptions for structured array (excluding main and subtitle which are handled separately)
    const allDescriptions = event._complexDescription?.descriptions || [];
    const excludedTypes = [
      DescriptionType.CollectiveDescriptionBody, 
      DescriptionType.Subtitle,
      'main',      // Also filter 'main' string in case it comes from draft API
      'subtitle'   // Also filter 'subtitle' string in case it comes from draft API
    ];
    for (const desc of allDescriptions) {
      if (!excludedTypes.includes(desc.type as string)) {
        structuredDescriptions.push({
          languageIso: desc.languageIso || 'en',
          type: desc.type,
          contentType: desc.contentType || 'plain_text',
          content: desc.content || ''
        });
      }
    }
  } catch {
    // Fallback if description model doesn't have these methods
    descriptionText = '';
  }

  // Get address string
  let addressString = '';
  if (event._address) {
    const parts = [
      event._address.street,
      event._address.zip,
      event._address.locality || event._address.region
    ].filter(Boolean);
    addressString = parts.join(', ');
  }

  return {
    title: event._title || '',
    subtitle: subtitleText || undefined,
    descriptions: structuredDescriptions.length > 0 ? structuredDescriptions as EventFormData['descriptions'] : undefined,
    categories: event._categories?.map(cat => cat?.slug || cat?.id).filter(Boolean) || [],
    dates: event._dates?.map(date => ({
      start: date.start instanceof Date ? date.start : new Date(date.start),
      end: date.end instanceof Date ? date.end : new Date(date.end)
    })) || [],
    location: {
      name: event._locationName || '',
      address: addressString,
      coordinates: {
        lat: event._location?.lat || 0,
        lng: event._location?.lng || 0
      }
    },
    description: descriptionText,
    images: event._images
      ?.filter(img => img.url && !img.url.startsWith('blob:') && !isVideoLinkUrl(img.url))
      .map(img => ({
        url: img.url,
        caption: img.text, // ts-core uses 'text' for caption
        text: img.text,
        copyright: img.copyright
      })) || [],
    youtubeLinks: event._images
      ?.filter(img => img.url && isVideoLinkUrl(img.url))
      .map(img => img.url) || [],
    contactInfos: event._contacts?.map(contact => ({
      type: contact.type as 'email' | 'phone' | 'website',
      content: contact.content || ''
    })) || [],
    tags: event._tags || [],
    organisers: event._organizer ? [{
      name: event._organizer.name,
      salutation: event._organizer.salutation,
      location: event._organizer.location?.map((loc) => ({
        name: loc.name || '',
        address: {
          street: loc.address?.street || '',
          zip: loc.address?.zip || '',
          locality: loc.address?.locality || '',
          country: loc.address?.country || '',
          region: loc.address?.region || ''
        },
        coordinates: loc.coordinates || { lat: 0, lng: 0 }
      })),
      contactInfos: event._organizer.contacts?.map((c) => ({
        type: c.type as 'email' | 'phone' | 'website',
        content: c.content || ''
      }))
    }] : [],
    isDraft,
    draftId: draftId || (isDraft ? event._id : undefined),
    editId: isDraft ? undefined : event._id
  };
}

/**
 * Transform EventFormData to draft API payload format for saving
 */
export function transformFormDataToDraftPayload(formData: EventFormData): {
  type: 'balloon';
  draft: DraftApiData;
} {
  const isValidDate = (value: unknown): value is Date => {
    if (!(value instanceof Date)) return false;
    return Number.isFinite(value.getTime());
  };

  return {
    type: 'balloon',
    draft: {
      id: formData.draftId || '',
      name: formData.title,
      categories: formData.categories.map(slug => ({ slug })),
      descriptions: [
        // Main description from BasicInfoStep
        ...(formData.description ? [{ language: 'en', text: formData.description, type: 'main' }] : []),
        // Subtitle from DetailsStep
        ...(formData.subtitle ? [{ language: 'en', text: formData.subtitle, type: 'subtitle' }] : []),
        // Additional descriptions from DetailsStep (teaser, venue-info, etc.)
        ...(formData.descriptions?.map(d => ({
          language: d.languageIso || 'en',
          text: d.content,
          type: d.type
        })) || [])
      ],
      dates: formData.dates
        .filter(date => isValidDate(date.start))
        .map(date => ({
          startDate: date.start.toISOString(),
          // When no end is explicitly set, fall back to start — matches the
          // "end === start signals 'no end set'" convention from WhenWhereStep.
          endDate: isValidDate(date.end) ? date.end.toISOString() : date.start.toISOString()
        })),
      location: formData.location.coordinates.lat !== 0 ? [{
        name: formData.location.name,
        address: {
          street: formData.location.addressComponents?.street || formData.location.address,
          zip: formData.location.addressComponents?.zip || '',
          locality: formData.location.addressComponents?.locality || '',
          country: formData.location.addressComponents?.country || '',
          region: formData.location.addressComponents?.region || ''
        },
        coordinates: {
          lat: formData.location.coordinates.lat,
          lng: formData.location.coordinates.lng
        }
      }] : [],
      images: [
        ...(formData.images
          ?.filter(img => img.url && !img.url.startsWith('blob:'))
          .map(img => ({
            url: img.url,
            caption: img.caption || img.text,
            copyright: img.copyright
          })) || []),
        ...(formData.youtubeLinks
          ?.filter(url => url && isVideoLinkUrl(url))
          .map(url => ({ url, caption: undefined, copyright: undefined })) || []),
      ],
      tags: formData.tags || [],
      contactInfos: formData.contactInfos?.map(c => ({
        type: c.type,
        content: c.content
      })) || [],
      organisers: formData.organisers?.filter(o => o.name || o.salutation || o.location?.length || o.contactInfos?.length).map(o => ({
        name: o.name,
        salutation: o.salutation,
        location: o.location?.map(loc => ({
          name: loc.name,
          address: loc.address,
          coordinates: loc.coordinates
        })),
        contactInfos: o.contactInfos?.map(c => ({
          type: c.type,
          content: c.content
        }))
      })) || []
    }
  };
}
